import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import AvatarPreview from "../avatar-preview";
import AvatarCreator from "../../onland/react-components/avatar-creator";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  makeStyles
} from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { ensureAvatarMaterial } from "../../utils/avatar-utils";
import { upload } from "../../utils/media-utils";
import { fetchReticulumAuthenticated } from "../../utils/phoenix-utils";
import { getSignedUrl } from "../../utils/artwork-utils";
import { getCookieToken } from "../../utils/get-auth-user";
import configs from "../../utils/configs";

const useStyles = makeStyles({
  textField: {
    width: "100%",
    marginBottom: "10px"
  },
  avatarEditor: {
    position: "fixed",
    left: "0",
    right: "0",
    top: "0",
    bottom: "0",
    zIndex: "9999999",
    pointerEvents: "auto",
    backgroundColor: "var(--primaryColorBackground)"
  }
});

class GLTFBinarySplitterPlugin {
  constructor(parser) {
    this.parser = parser;
    this.gltf = null;
    this.bin = null;
  }

  beforeRoot() {
    const parser = this.parser;
    const { body } = parser.extensions.KHR_binary_glTF;
    const content = JSON.stringify(ensureAvatarMaterial(parser.json));

    this.gltf = new File([content], "file.gltf", {
      type: "model/gltf"
    });
    this.bin = new File([body], "file.bin", {
      type: "application/octet-stream"
    });

    // This plugin just wants to split gltf and bin from glb and
    // doesn't want to start the parse. But glTF loader plugin API
    // doesn't have an ability to cancel the parse. So overriding
    // parser.json with very light glTF data as workaround.
    parser.json = { asset: { version: "2.0" } };
  }

  afterRoot(result) {
    result.files = result.files || {};
    result.files.gltf = this.gltf;
    result.files.bin = this.bin;
  }
}

const AVATARS_API = "/api/v1/avatars";

export function FluentAvatarEditor({ onSave, creationModal, setCreationModal, uploadProgress, setUploadProgress }) {
  const [avatar, setAvatar] = useState({
    name: "My Avatar",
    files: {},
    parent_avatar_listing_id: ""
  });

  const [previewGltfUrl, setPreviewGltfUrl] = useState("");
  const [inputFiles, setInputFiles] = useState({
    glb: null,
    bin: null,
    thumbnail: null,
    gltf: null
  });
  const [errorUploading, setErrorUploading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showModalSave, setShowModalSave] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (creationModal === "upload") {
      inputRef.current.click();
      setCreationModal("");
    }
  }, [creationModal, setCreationModal]);

  const styles = useStyles();

  const calculateUploadProgress = (loaded, totalBytes, currProgress = 0) => {
    if (totalBytes > 0 && loaded >= 0 && loaded <= totalBytes) {
      const progress = (loaded / totalBytes) * 100 + parseFloat(currProgress);
      return progress.toFixed(2); // Returns the progress as a string with two decimal places.
    } else {
      // Handle invalid input values by returning an error message or throwing an exception.
      console.error("Invalid input for progress calculation.");
      return null;
    }
  };

  const getAvatarFiles = gltfUrl => {
    const gltfLoader = new GLTFLoader().register(parser => new GLTFBinarySplitterPlugin(parser));

    const onProgress = event => {
      const { loaded, total } = event;
      const progress = calculateUploadProgress(loaded, total, uploadProgress, 80.0);
      if (progress) {
        setUploadProgress(progress);
      }
    };

    return new Promise((resolve, reject) => {
      // GLTFBinarySplitterPlugin saves gltf and bin in gltf.files
      gltfLoader.load(
        gltfUrl,
        result => {
          setInputFiles(prevInputFiles => ({
            ...prevInputFiles,
            bin: result.files.bin,
            gltf: result.files.gltf
          }));
          resolve(result);
        },
        onProgress,
        reject
      );
    });
  };

  const handleAvatarCreated = async gltfBlob => {
    try {
      const timestap = new Date().getTime();
      const id = timestap.toString().replace(/ /g, "_");
      const fileName = `avatar_${id}.glb`;
      const token = getCookieToken();

      const file = new File([gltfBlob], fileName);

      const uploadSignedUrlData = await getSignedUrl({
        file,
        token,
        folder: "avatars",
        apiUrl: configs["CUSTOM_API_URL"]
      });

      // Upload file
      await fetch(uploadSignedUrlData.presigned_url, {
        method: "PUT",
        headers: {
          "Content-Type": "image/png",
          accept: "application/json",
          "x-ms-blob-type": "BlockBlob"
        },
        body: file
      });

      const gltfUrl = uploadSignedUrlData.download_url;

      setAvatar({
        ...avatar,
        parent_avatar_listing_id: "",
        files: {
          ...avatar.files,
          glb: gltfUrl
        }
      });

      setUploading(true);
      setPreviewGltfUrl(gltfUrl);

      setInputFiles(prevInputFiles => ({
        ...prevInputFiles,
        glb: gltfUrl
      }));

      setShowModalSave(true);
      setCreationModal("");
    } catch (e) {
      setUploading(false);
      setCreationModal("");
      setErrorUploading(true);
      console.error(e);
    }
  };

  const createOrUpdateAvatar = async avatar => {
    const response = await fetchReticulumAuthenticated(AVATARS_API, "POST", {
      avatar
    });
    return response.avatars[0];
  };

  const uploadAvatar = async e => {
    e.preventDefault();
    setShowModalSave(false);

    let result;

    try {
      setUploading(true);

      if (inputFiles.glb) {
        const gltfLoader = new GLTFLoader().register(parser => new GLTFBinarySplitterPlugin(parser));
        const gltfUrl = inputFiles.glb instanceof File ? URL.createObjectURL(inputFiles.glb) : inputFiles.glb;

        const onProgress = data => {
          const { loaded, total } = data;
          const progress = calculateUploadProgress(loaded, total, uploadProgress, 33.33);

          if (progress) {
            setUploadProgress(progress);
          }
        };

        result = await new Promise((resolve, reject) => {
          // GLTFBinarySplitterPlugin saves gltf and bin in gltf.files
          gltfLoader.load(
            gltfUrl,
            result => {
              resolve(result);
            },
            onProgress,
            reject
          );
        });

        URL.revokeObjectURL(gltfUrl);
      }

      if (inputFiles.glb && "string" === typeof inputFiles.glb && !result.gltf && !result.bin) {
        await getAvatarFiles(inputFiles.glb);
      }
    } catch (e) {
      console.error("error", e);
      setUploading(false);
      setUploadProgress(null);
      setErrorUploading(true);
    }

    const thumbnail = await preview.snapshot();

    const newInputFiles = {
      gltf: result.files.gltf,
      bin: result.files.bin,
      thumbnail: new File([thumbnail], "thumbnail.png", {
        type: "image/png"
      })
    };

    setInputFiles(prevInputFiles => ({
      ...prevInputFiles,
      ...newInputFiles
    }));

    const filesToUpload = ["gltf", "bin", "base_map", "emissive_map", "normal_map", "orm_map", "thumbnail"].filter(
      k => newInputFiles[k] === null || newInputFiles[k] instanceof File
    );

    const fileUploads = await Promise.all(filesToUpload.map(f => newInputFiles[f] && upload(newInputFiles[f])));

    const avatarToCreate = {
      ...avatar,
      attributions: {
        creator: avatar.creatorAttribution
      },
      files: fileUploads
        .map((resp, i) => [filesToUpload[i], resp && [resp.file_id, resp.meta.access_token, resp.meta.promotion_token]])
        .reduce((o, [k, v]) => ({ ...o, [k]: v }), {})
    };
    try {
      const newAvatar = await createOrUpdateAvatar(avatarToCreate);

      if (onSave) {
        onSave(newAvatar);
      }

      setAvatar({
        name: "My Avatar",
        files: {},
        parent_avatar_listing_id: ""
      });
      setPreviewGltfUrl("");
      setInputFiles({
        glb: null,
        bin: null,
        thumbnail: null,
        gltf: null
      });
    } catch (e) {
      console.error("error", e);
      setUploading(false);
      setUploadProgress(null);
      setErrorUploading(true);
    }
  };

  const intl = useIntl();

  const handleInputChange = e => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      e.target.value = null;
      setInputFiles(prevInputFiles => ({ ...prevInputFiles, glb: file }));
      URL.revokeObjectURL(avatar.files["glb"]);
      setAvatar({
        ...avatar,
        files: {
          ...avatar.files,
          glb: URL.createObjectURL(file)
        },
        parent_avatar_listing_id: ""
      });
      setPreviewGltfUrl(URL.createObjectURL(file));
      setShowModalSave(true);
    }
  };

  return (
    <>
      {previewGltfUrl && (
        <AvatarPreview
          className="preview hide"
          avatarCreator={true}
          avatarGltfUrl={previewGltfUrl || inputFiles.glb}
          progress={uploadProgress}
          hasError={errorUploading}
          ref={p => setPreview(p)}
        />
      )}
      {creationModal === "cartoons" && (
        <AvatarCreator
          className={styles.avatarEditor}
          onClose={() => setCreationModal("")}
          onCreate={handleAvatarCreated}
          modal="cartoons"
        />
      )}

      <input
        style={{ display: "none" }}
        ref={inputRef}
        id="avatar-file_glb"
        type="file"
        accept="model/gltf+binary,.glb"
        onChange={handleInputChange}
      />

      <Dialog open={showModalSave} onOpenChange={(_, data) => setShowModalSave(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label={intl.formatMessage({
                    id: "room-settings-sidebar.close-button",
                    defaultMessage: "Close"
                  })}
                  icon={<Dismiss24Regular />}
                  onClick={() => setShowModalSave(false)}
                />
              }
            >
              <FormattedMessage id="avatar-editor.save-avatar-button" defaultMessage="Save avatar" />
            </DialogTitle>
            <DialogContent>
              <Input
                appearance="filled-darker"
                required
                value={avatar?.name || ""}
                onChange={e => setAvatar({ ...avatar, name: e.target.value })}
                placeholder={intl.formatMessage({
                  id: "avatar-editor.field.avatar-name",
                  defaultMessage: "Avatar name"
                })}
                className={styles.textField}
              />
              <p>
                <FormattedMessage
                  id="avatar-editor.save-avatar-message"
                  defaultMessage="We're almost ready! To complete the creation you must enter a name and save your avatar."
                />
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="outline">
                  <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                </Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={uploadAvatar}>
                <FormattedMessage id="avatar-editor.submit-button.save" defaultMessage="Save" />
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

FluentAvatarEditor.propTypes = {
  onSave: PropTypes.func,
  creationModal: PropTypes.string,
  setCreationModal: PropTypes.func,
  uploadProgress: PropTypes.string,
  setUploadProgress: PropTypes.func
};
